import { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser, RefreshCw } from "lucide-react";

/**
 * Touch-friendly signature pad using an HTML canvas. Supports mouse + touch + pen.
 * Ref API: getDataURL() → string | null (null if empty)
 *          clear()      → void
 *          isEmpty      → boolean
 */
const SignaturePad = forwardRef(function SignaturePad({ height = 200 }, ref) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const empty = useRef(true);
  const last = useRef({ x: 0, y: 0 });
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const w = wrap.clientWidth;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B2A4A";
    // Fill white background so the exported PNG is not transparent
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    empty.current = true;
  }, [renderKey, height]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    empty.current = false;
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  useImperativeHandle(ref, () => ({
    getDataURL: () => {
      if (empty.current) return null;
      return canvasRef.current.toDataURL("image/png");
    },
    clear: () => setRenderKey((k) => k + 1),
    get isEmpty() { return empty.current; },
  }), []);

  return (
    <div className="w-full">
      <div ref={wrapRef} className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
        <canvas
          key={renderKey}
          ref={canvasRef}
          data-testid="signature-pad"
          className="block touch-none select-none"
          style={{ touchAction: "none" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Firma con el dedo</span>
        <button
          type="button"
          onClick={() => setRenderKey((k) => k + 1)}
          data-testid="signature-clear"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-rose-600"
        >
          <Eraser size={14} /> Borrar
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
